<script setup lang="ts">
import EntityChange from '@/components/EntityHistory/EntityChange.vue';
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import { useNotificationStore } from '@/stores/notificationStore';
import { useQaEquipmentStore } from '@/stores/qaEquipmentStore';
import { truthy } from '@/utils/helper';
import { I18nKey } from '@/utils/typings';
import { reloadEquipmentKey } from '@/views/qaEquipment/types';
import { successfullyDeRegisteredMsg } from '@/views/qaEquipment/utils';
import { useTranslation } from 'i18next-vue';
import { getEntityChangeEvent } from '@/components/EntityHistory/entityChange';
import { computed, inject, toRef } from 'vue';
import Button from '@/components/buttons/Button.vue';
import { useDeviceStatusSignalR } from '@/components/deviceStatus/utils/useDeviceStatusSignalR';
import Tooltip from '@/components/Tooltip/Tooltip.vue';

const { t } = useTranslation();
const notificationStore = useNotificationStore();
const qaEquipmentStore = useQaEquipmentStore();
const deviceState = useDeviceStatusSignalR(toRef(() => qaEquipmentStore.deviceForRegistration?.id));

const reloadEquipment = inject(reloadEquipmentKey);

const { deregister } = qaEquipmentStore;
qaEquipmentStore.deRegistrationSuccess = () => {
  if (qaEquipmentStore.deviceForRegistration) {
    notificationStore.success(successfullyDeRegisteredMsg(qaEquipmentStore.deviceForRegistration));
    qaEquipmentStore.setDeviceForRegistration(undefined);
  }
  reloadEquipment?.();
};
qaEquipmentStore.deRegistrationFailure = () => {
  if (qaEquipmentStore.deviceForRegistration) {
    const errorMessage = qaEquipmentStore.detailedError?.message;
    if (typeof errorMessage === 'string') {
      notificationStore.error(errorMessage);
    } else if (errorMessage?.key) {
      notificationStore.error(t(errorMessage.key as I18nKey));
    }
    qaEquipmentStore.resetErrorMessage();
  }
};
const canBeDeRegistered = computed(
  () =>
    deviceState.value?.state &&
    (deviceState.value.state === 'Measuring' || deviceState.value.state === 'Initializing'),
);
</script>

<template>
  <FormLabel
    v-if="
      qaEquipmentStore.deviceForRegistration &&
      'registrationState' in qaEquipmentStore.deviceForRegistration &&
      'registrationTime' in qaEquipmentStore.deviceForRegistration &&
      truthy(qaEquipmentStore.deviceForRegistration.registrationTime) &&
      (qaEquipmentStore.deviceForRegistration.registrationState === 'Registered' ||
        qaEquipmentStore.deviceForRegistration.registrationState === 'DeRegistered')
    "
    :label="
      qaEquipmentStore.deviceForRegistration.registrationState === 'DeRegistered'
        ? $t('DeRegistered')
        : $t('Registered')
    "
    class="gutterTop"
  >
    <div class="text-body-2">
      <EntityChange
        :entityChange="getEntityChangeEvent(qaEquipmentStore.deviceForRegistration, 'registered')"
      />
    </div>
    <Tooltip
      position="left"
      :disabled="qaEquipmentStore.deRegistrationPending || !canBeDeRegistered"
    >
      <template #target="{ props: targetProps }">
        <div v-bind="targetProps">
          <Button
            v-if="qaEquipmentStore.deviceForRegistration?.registrationState === 'Registered'"
            variant="primary"
            class="mt-4"
            block
            :label="$t('Unregister')"
            :loading="qaEquipmentStore.deRegistrationPending"
            :disabled="qaEquipmentStore.deRegistrationPending || canBeDeRegistered"
            @click="deregister(qaEquipmentStore.deviceForRegistration)"
          />
        </div>
      </template>
      <template #tooltip>{{ $t('TheDeviceCanNotBeDerigsteredWhileInUse') }}</template>
    </Tooltip>
  </FormLabel>
</template>
